/**
 * View Template Page
 * Full page for viewing template details
 */

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Edit, 
  Copy, 
  Trash2, 
  Globe, 
  Calendar,
  FileText,
  CheckCircle,
  Circle,
  FolderOpen,
  Clock,
  User,
} from 'lucide-react';
import { useTemplates } from '@/hooks/useTemplates';
import { DeleteConfirmationModal } from '@/components/be-study/modals/ConfirmationModal';
import { COUNTRIES, formatDate, formatDateTime } from '@/constants/beStudyMockData';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';

/**
 * Section Card Component
 */
function SectionCard({ section, index }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold shrink-0">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium">{section.title}</h4>
          {section.required ? (
            <Badge variant="default" className="text-xs">Required</Badge>
          ) : (
            <Badge variant="outline" className="text-xs">Optional</Badge>
          )}
        </div>
        {section.description && (
          <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Info Item Component
 */
function InfoItem({ icon: Icon, label, value, className }) {
  return (
    <div className={cn("flex items-start gap-3", className)}>
      <div className="p-2 rounded-lg bg-muted shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value || '-'}</p>
      </div>
    </div>
  );
}

/**
 * View Template Page Component
 */
export function ViewTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getTemplate, deleteTemplate } = useTemplates();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const template = getTemplate(id);
  const country = COUNTRIES.find(c => c.code === template?.country);

  // Handle template not found
  if (!template) {
    return (
      <BaseLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <FolderOpen className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Template Not Found</h2>
          <p className="text-muted-foreground mb-4">The template you're looking for doesn't exist.</p>
          <Button onClick={() => navigate(ROUTES.TEMPLATES)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </div>
      </BaseLayout>
    );
  }

  const handleDelete = () => {
    deleteTemplate(template.id);
    setShowDeleteModal(false);
    navigate(ROUTES.TEMPLATES);
  };

  const handleEdit = () => {
    navigate(`/admin/templates/${template.id}/edit`);
  };

  const requiredSections = template.sections?.filter(s => s.required) || [];
  const optionalSections = template.sections?.filter(s => !s.required) || [];

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="view-template-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(ROUTES.TEMPLATES)}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{template.name}</h1>
                <Badge variant={template.status === 'ACTIVE' ? 'default' : 'secondary'}>
                  {template.status}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">
                Version {template.version} • {country?.name || template.country}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button variant="outline" size="sm" onClick={handleEdit} data-testid="edit-btn">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-red-600 hover:text-red-700"
              onClick={() => setShowDeleteModal(true)}
              data-testid="delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Template Info */}
          <div className="space-y-6">
            {/* Basic Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Template Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoItem
                  icon={Globe}
                  label="Country/Region"
                  value={country?.name || template.country}
                />
                <InfoItem
                  icon={FileText}
                  label="Version"
                  value={template.version}
                />
                <InfoItem
                  icon={Calendar}
                  label="Created"
                  value={formatDate(template.createdAt)}
                />
                <InfoItem
                  icon={Clock}
                  label="Last Updated"
                  value={formatDateTime(template.updatedAt)}
                />
              </CardContent>
            </Card>

            {/* Description Card */}
            {template.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{template.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Statistics Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold">{template.sections?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Sections</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold">{requiredSections.length}</p>
                    <p className="text-xs text-muted-foreground">Required</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Sections */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Sections</CardTitle>
                    <CardDescription>
                      {template.sections?.length || 0} sections in this template
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  <div className="space-y-3">
                    {template.sections?.map((section, index) => (
                      <SectionCard 
                        key={section.id} 
                        section={section} 
                        index={index}
                      />
                    ))}

                    {(!template.sections || template.sections.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">No sections defined</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          onConfirm={handleDelete}
          itemName={template.name}
          itemType="template"
        />
      </div>
    </BaseLayout>
  );
}

export default ViewTemplatePage;
